(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  const saveDataEnabled = () => Boolean(connection && connection.saveData);
  const navigationStatus = document.querySelector('[data-ev-navigation-status]');
  const navigationExclusions = [
    '[data-ev-no-page-loader]',
    'cart-drawer',
    '.cart-drawer',
    'cart-notification',
    '.cart-notification',
    'predictive-search',
    '.predictive-search',
    'facet-filters-form',
    '.facets',
    '.facets-container',
    '.mobile-facets',
    'localization-form',
    'product-form',
    'variant-selects',
    '.product__accordion',
  ].join(',');
  let navigationDelay;
  let navigationGuard;
  let navigationTarget;

  const clearNavigationState = () => {
    window.clearTimeout(navigationDelay);
    window.clearTimeout(navigationGuard);
    navigationTarget?.removeAttribute('data-ev-navigation-target');
    navigationTarget = null;
    root.classList.remove('is-navigation-pending', 'is-navigation-slow');
    if (navigationStatus) navigationStatus.hidden = true;
  };

  const eligibleNavigationLink = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !(event.target instanceof Element)
    ) {
      return null;
    }

    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return null;
    if (link.matches('[aria-disabled="true"]') || link.closest(navigationExclusions)) return null;
    if (link.id === 'cart-icon-bubble' && document.querySelector('cart-drawer')) return null;

    const rawHref = link.getAttribute('href');
    if (!rawHref || rawHref.startsWith('#')) return null;

    const destination = new URL(link.href, window.location.href);
    if (!['http:', 'https:'].includes(destination.protocol) || destination.origin !== window.location.origin) return null;
    if (
      destination.pathname === window.location.pathname &&
      destination.search === window.location.search &&
      destination.hash
    ) {
      return null;
    }
    if (destination.href === window.location.href) return null;

    return link;
  };

  const beginNavigationState = (link) => {
    clearNavigationState();
    navigationTarget = link;
    navigationTarget.setAttribute('data-ev-navigation-target', 'true');
    root.classList.add('is-navigation-pending');

    navigationDelay = window.setTimeout(() => {
      root.classList.add('is-navigation-slow');
      if (navigationStatus) navigationStatus.hidden = false;
    }, 225);
    navigationGuard = window.setTimeout(clearNavigationState, 10000);
  };

  const handleNavigationClick = (event) => {
    const link = eligibleNavigationLink(event);
    if (link) beginNavigationState(link);
  };

  const revealHero = (hero) => {
    if (hero.dataset.evRevealReady === 'true') return;
    hero.dataset.evRevealReady = 'true';
    if (reducedMotion.matches || saveDataEnabled()) {
      hero.classList.add('is-revealed');
      return;
    }

    hero.classList.add('is-reveal-enhanced');
    const media = hero.querySelector('.ev-hero__media img, .ev-video-hero__poster img');
    let fallbackTimer;

    const finishReveal = (event) => {
      if (event?.type === 'error') hero.classList.add('is-poster-failed');
      window.clearTimeout(fallbackTimer);
      media?.removeEventListener('load', finishReveal);
      media?.removeEventListener('error', finishReveal);
      requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add('is-revealed')));
    };

    if (!media || media.complete) {
      finishReveal(media && media.naturalWidth === 0 ? { type: 'error' } : undefined);
    } else {
      media.addEventListener('load', finishReveal, { once: true });
      media.addEventListener('error', finishReveal, { once: true });
      fallbackTimer = window.setTimeout(finishReveal, 400);
    }

    hero.evRevealCleanup = () => {
      window.clearTimeout(fallbackTimer);
      media?.removeEventListener('load', finishReveal);
      media?.removeEventListener('error', finishReveal);
    };
  };

  const setControlLabel = (button, label) => {
    if (!button || !label) return;
    button.setAttribute('aria-label', label);
    const text = button.querySelector('[data-control-label]');
    if (text) text.textContent = label;
  };

  const setupImageFallbacks = (scope) => {
    const selector = [
      '.ev-hero img',
      '.ev-split img',
      '.ev-tile img',
      '.ev-story img',
      '.collection-hero img',
      '.card__media img',
      '.product__media img',
    ].join(',');

    scope.querySelectorAll(selector).forEach((image) => {
      if (image.dataset.evImageFallbackReady === 'true') return;
      image.dataset.evImageFallbackReady = 'true';
      const markFailed = () => image.setAttribute('data-ev-image-failed', 'true');
      image.addEventListener('error', markFailed, { once: true });
      if (image.complete && image.naturalWidth === 0) markFailed();
    });
  };

  const setupVideoHero = (hero) => {
    if (hero.dataset.evVideoReady === 'true') return;
    hero.dataset.evVideoReady = 'true';

    const video = hero.querySelector('video');
    const playButton = hero.querySelector('[data-video-play-pause]');
    const muteButton = hero.querySelector('[data-video-mute]');
    const controls = hero.querySelector('[data-video-controls]');
    const posterImage = hero.querySelector('.ev-video-hero__poster img');

    if (!video) {
      controls?.setAttribute('hidden', '');
      return;
    }

    const labels = {
      play: playButton?.dataset.labelPlay,
      pause: playButton?.dataset.labelPause,
      mute: muteButton?.dataset.labelMute,
      unmute: muteButton?.dataset.labelUnmute,
    };
    let manuallyPaused = false;
    let wasPlayingBeforeExit = false;
    let autoplayAttempted = false;
    let userStarted = false;
    let isIntersecting = false;
    let mediaFailed = false;

    video.muted = true;
    video.playsInline = true;

    const sync = () => {
      const playing = !video.paused && !video.ended;
      hero.classList.toggle('is-playing', playing);
      setControlLabel(playButton, playing ? labels.pause : labels.play);
      setControlLabel(muteButton, video.muted ? labels.unmute : labels.mute);
      muteButton?.setAttribute('aria-pressed', String(!video.muted));
    };

    const play = async (isAutomatic = false) => {
      if (mediaFailed || (isAutomatic && (reducedMotion.matches || saveDataEnabled()))) return;
      try {
        await video.play();
        if (!isAutomatic) {
          manuallyPaused = false;
          userStarted = true;
        }
      } catch {
        hero.classList.remove('is-playing');
      }
      sync();
    };

    const handlePlayClick = () => {
      if (video.paused) {
        play(false);
      } else {
        manuallyPaused = true;
        userStarted = false;
        wasPlayingBeforeExit = false;
        video.pause();
      }
    };

    const handleMuteClick = () => {
      video.muted = !video.muted;
      sync();
    };

    const stateEvents = ['play', 'playing', 'pause', 'ended', 'volumechange'];
    stateEvents.forEach((eventName) => {
      video.addEventListener(eventName, sync);
    });
    playButton?.addEventListener('click', handlePlayClick);
    muteButton?.addEventListener('click', handleMuteClick);

    const handleMediaError = () => {
      mediaFailed = true;
      wasPlayingBeforeExit = false;
      userStarted = false;
      video.pause();
      hero.classList.remove('is-playing');
      hero.classList.add('is-media-failed');
      controls?.setAttribute('hidden', '');
      sync();
    };
    const handlePosterError = () => hero.classList.add('is-poster-failed');
    video.addEventListener('error', handleMediaError);
    posterImage?.addEventListener('error', handlePosterError);

    const canStartAutomatically = () =>
      hero.dataset.autoplay === 'true' && !reducedMotion.matches && !saveDataEnabled();
    const canResume = () => userStarted || canStartAutomatically();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isIntersecting = entry.isIntersecting;
          if (entry.isIntersecting) {
            if (canStartAutomatically() && !autoplayAttempted) {
              autoplayAttempted = true;
              play(true);
            } else if (wasPlayingBeforeExit && !manuallyPaused && canResume()) {
              wasPlayingBeforeExit = false;
              play(!userStarted);
            }
          } else if (!video.paused) {
            wasPlayingBeforeExit = true;
            video.pause();
          }
        });
      },
      { threshold: 0.12 },
    );
    observer.observe(hero);

    const handleVisibilityChange = () => {
      if (document.hidden && !video.paused) {
        wasPlayingBeforeExit = true;
        video.pause();
      } else if (!document.hidden && isIntersecting && wasPlayingBeforeExit && !manuallyPaused && canResume()) {
        wasPlayingBeforeExit = false;
        play(!userStarted);
      }
    };

    const handleMotionPreferenceChange = () => {
      if (reducedMotion.matches) {
        if (!video.paused) video.pause();
        wasPlayingBeforeExit = false;
        userStarted = false;
        sync();
      } else if (isIntersecting && !manuallyPaused && !autoplayAttempted && canStartAutomatically()) {
        autoplayAttempted = true;
        play(true);
      }
    };

    const handleConnectionChange = () => {
      if (saveDataEnabled()) {
        if (!video.paused) video.pause();
        wasPlayingBeforeExit = false;
        userStarted = false;
        sync();
      } else if (isIntersecting && !manuallyPaused && !autoplayAttempted && canStartAutomatically()) {
        autoplayAttempted = true;
        play(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    reducedMotion.addEventListener('change', handleMotionPreferenceChange);
    connection?.addEventListener('change', handleConnectionChange);

    hero.evVideoCleanup = () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      reducedMotion.removeEventListener('change', handleMotionPreferenceChange);
      connection?.removeEventListener('change', handleConnectionChange);
      playButton?.removeEventListener('click', handlePlayClick);
      muteButton?.removeEventListener('click', handleMuteClick);
      video.removeEventListener('error', handleMediaError);
      posterImage?.removeEventListener('error', handlePosterError);
      stateEvents.forEach((eventName) => video.removeEventListener(eventName, sync));
    };

    sync();
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-ev-reveal]').forEach(revealHero);
    scope.querySelectorAll('[data-ev-video-hero]').forEach(setupVideoHero);
    setupImageFallbacks(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('click', handleNavigationClick);
  window.addEventListener('pageshow', clearNavigationState);
  window.addEventListener('pagehide', clearNavigationState);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ev-reveal]').forEach((hero) => hero.evRevealCleanup?.());
    event.target.querySelectorAll('[data-ev-video-hero]').forEach((hero) => hero.evVideoCleanup?.());
  });
})();
